import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "../lib/auth";
import BillingBanner from "./BillingBanner";
import AttentionBar from "./AttentionBar";
import Footer from "./Footer";
import {
  LayoutDashboard,
  FileText,
  PlugZap,
  Activity,
  LogOut,
  BookOpen,
  ShieldCheck,
  Users,
  Settings,
  HelpCircle,
  BarChart3,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/vendors", label: "Vendor list", icon: ShieldCheck, testid: "nav-vendors" },
  { to: "/customers", label: "Customer list", icon: Users, testid: "nav-customers" },
  { to: "/mappings", label: "Mappings", icon: BookOpen, testid: "nav-mappings" },
  { to: "/connections", label: "Connections", icon: PlugZap, testid: "nav-connections" },
  { to: "/activity", label: "Activity", icon: Activity, testid: "nav-activity" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside className="w-64 border-r border-neutral-200 bg-white flex flex-col">
        <div className="px-5 py-6 border-b border-neutral-900">
          <Link to="/dashboard" className="block" data-testid="sidebar-logo">
            <Logo className="h-7" testid="sidebar-logo-img" />
          </Link>
          <div className="label-mini mt-2">the erp interpreter</div>
        </div>

        <nav className="flex-1 py-4" data-testid="sidebar-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              <item.icon size={14} strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
          {user?.role === "super_admin" ? (
            <NavLink to="/admin/usage"
                     data-testid="nav-admin-usage"
                     className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <BarChart3 size={14} strokeWidth={2} />
              Usage
            </NavLink>
          ) : null}
          {user?.role === "super_admin" ? (
            <NavLink to="/diagnostics"
                     data-testid="nav-diagnostics"
                     className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <Activity size={14} strokeWidth={2} />
              Diagnostics
            </NavLink>
          ) : null}
          <NavLink to="/help"
                   data-testid="nav-help"
                   className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
            <HelpCircle size={14} strokeWidth={2} />
            Help
          </NavLink>
        </nav>

        <div className="border-t border-neutral-200 p-4">
          <div className="label-mini">company</div>
          <div className="mt-1 text-xs truncate font-display font-semibold" data-testid="current-company-name">
            {user?.role === "super_admin" ? "All companies (super admin)" : (user?.company?.name || "—")}
          </div>
          <div className="label-mini mt-3">signed in as</div>
          <div className="mt-1 text-xs truncate" data-testid="current-user-email">
            {user?.email}
          </div>
          <button
            type="button"
            className="btn-outline mt-3 w-full flex items-center justify-center gap-2"
            onClick={onLogout}
            data-testid="logout-button"
          >
            <LogOut size={12} />
            sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden flex flex-col">
        <AttentionBar />
        {user?.company_id && user?.role !== "super_admin" ? <BillingBanner /> : null}
        <div className="flex-1">{children}</div>
        <Footer />
      </main>
    </div>
  );
}
